import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAdminAuthorization } from '../contexts/AdminAuthorizationContext';

export interface AdminPermissions {
  canManageUsers: boolean;
  canManageOrganizations: boolean;
  canManageRoles: boolean;
  canViewAnalytics: boolean;
  isAdmin: boolean;
}

export const useAdminPermissionsNew = (): AdminPermissions => {
  const { user, isSessionReady } = useAuth();
  const { hasPrivilege, loading: authLoading } = useAdminAuthorization();
  
  // Add a safety timeout to prevent infinite loading
  const [timeoutReached, setTimeoutReached] = React.useState(false);
  
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setTimeoutReached(true);
    }, 5000);
    
    return () => clearTimeout(timer);
  }, []);

  // Only calculate permissions when session is ready
  if (!isSessionReady || !user) {
    return {
      canManageUsers: false,
      canManageOrganizations: false,
      canManageRoles: false,
      canViewAnalytics: false,
      isAdmin: false
    };
  }

  // Wait for authorization to be ready, but with safety timeout
  if (authLoading && !timeoutReached) {
    return {
      canManageUsers: false,
      canManageOrganizations: false,
      canManageRoles: false,
      canViewAnalytics: false,
      isAdmin: false
    };
  }

  // REAL PERMISSION CHECKING: Use the admin authorization system
  const hasAdminAccess = hasPrivilege('ADMIN_ACCESS');
  const hasSuperAdmin = hasPrivilege('SUPER_ADMIN');
  const isAdmin = hasAdminAccess || hasSuperAdmin;

  if (isAdmin) {
    return {
      canManageUsers: hasPrivilege('CREATE_USER') || hasPrivilege('UPDATE_USER') || hasPrivilege('DELETE_USER'),
      canManageOrganizations: hasPrivilege('CREATE_ORGANIZATION') || hasPrivilege('UPDATE_ORGANIZATION') || hasPrivilege('DELETE_ORGANIZATION'),
      canManageRoles: hasPrivilege('CREATE_ROLE') || hasPrivilege('UPDATE_ROLE') || hasPrivilege('DELETE_ROLE'),
      canViewAnalytics: hasPrivilege('READ_ALL'),
      isAdmin: true
    };
  } else {
    // User doesn't have admin privileges
    return {
      canManageUsers: false,
      canManageOrganizations: false,
      canManageRoles: false,
      canViewAnalytics: false,
      isAdmin: false
    };
  }
};
